import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Loader2, CheckCircle2, CalendarDays } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import PhoneInput from './shared/PhoneInput';
import { formatPhone, telHref } from './shared/phone';
import { PAGE_PAUSED_COPY, isLinkPaused } from './errors';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PAGE_BG = '#faf7f2';
const INPUT = 'w-full border border-slate-200 rounded-lg px-3 py-2 text-sm';
const LP_DEFAULT_FIELDS = ['name', 'email', 'phone'];

function Notice({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: PAGE_BG }} data-testid="public-page-notice">
      <div className="text-slate-500">{children}</div>
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// A missing page (404) reads differently from a network or server failure.
const loadError = (e, what) => (e?.response?.status === 404
  ? `This ${what} isn't available.`
  : isLinkPaused(e) ? PAGE_PAUSED_COPY
  : `We couldn't load this ${what}. Check your connection and refresh to try again.`);

// Only an https link goes into an href (the API already filters; this is the second check).
const safeHref = (u) => (typeof u === 'string' && /^https:\/\//i.test(u) ? u : null);

/* PUBLIC agent-branded microsite at /agent/:slug */
export function AgentSitePublicPage() {
  const { slug } = useParams();
  const [site, setSite] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/re/agent/${slug}`).then(r => setSite(r.data)).catch((e) => setErr(loadError(e, 'site')));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${BACKEND}/api/re/agent/${slug}/lead`, form); setSubmitted(true); }
    catch (e) { toast({ title: 'Submission failed', description: isLinkPaused(e) ? PAGE_PAUSED_COPY : 'Please try again in a moment.' }); }
  };

  if (err) return <Notice>{err}</Notice>;
  if (!site) return <div className="min-h-screen flex items-center justify-center" style={{ background: PAGE_BG }}><Loader2 className="animate-spin" /></div>;

  const socials = [
    { key: 'facebook', label: 'Facebook', Icon: Facebook, href: safeHref(site.social_facebook) },
    { key: 'instagram', label: 'Instagram', Icon: Instagram, href: safeHref(site.social_instagram) },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, href: safeHref(site.social_linkedin) },
  ].filter((x) => x.href);
  const designations = site.designations || [];
  const languages = site.languages || [];
  const facts = [
    site.years_experience > 0 && `${site.years_experience} ${site.years_experience === 1 ? 'year' : 'years'} of experience`,
    languages.length > 0 && `Speaks ${languages.join(', ')}`,
  ].filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      {/* Hero */}
      <div className="text-white relative" style={{ background: site.hero_image_url ? `linear-gradient(rgba(3,5,36,0.8), rgba(7,11,95,0.9)), url(${site.hero_image_url}) center/cover` : 'linear-gradient(160deg, #030524 0%, #070B5F 60%, #0F1780 100%)' }}>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="text-xs uppercase tracking-widest" style={{ color: '#A6E56C' }}>Real Estate · Powered by Top Choice</div>
              <h1 className="font-serif text-4xl md:text-6xl font-bold mt-2">{site.display_name}</h1>
              {site.headline && <div className="mt-2 text-xl text-white/80">{site.headline}</div>}
              {site.tagline && <div className="mt-4 text-white/60 max-w-xl">{site.tagline}</div>}
              <div className="mt-6 flex gap-3 flex-wrap">
                {site.phone && <a href={telHref(site.phone)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold" style={{ background: '#8DC63F', color: '#070B5F' }}><Phone size={14} /> {formatPhone(site.phone)}</a>}
                {site.email && <a href={`mailto:${site.email}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold border border-white/20 hover:bg-white/10"><Mail size={14} /> Email</a>}
                {safeHref(site.calendly_url) && <a href={safeHref(site.calendly_url)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold border border-white/20 hover:bg-white/10" data-testid="agent-site-calendly"><CalendarDays size={14} /> Book a time</a>}
              </div>
              {socials.length > 0 && (
                <div className="mt-4 flex gap-3" data-testid="agent-site-socials">
                  {socials.map(({ key, label, Icon, href }) => (
                    <a key={key} href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens in a new tab)`} className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10">
                      <Icon size={16} />
                    </a>
                  ))}
                </div>
              )}
            </div>
            {site.headshot_url && (
              <div className="flex justify-center">
                <img src={site.headshot_url} alt={site.display_name} className="w-48 h-48 rounded-full object-cover border-4" style={{ borderColor: '#A6E56C' }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          {site.bio && (
            <section>
              <h2 className="font-serif text-2xl font-bold mb-3" style={{ color: '#070B5F' }}>About {site.display_name.split(' ')[0]}</h2>
              <div className="text-slate-600 leading-relaxed whitespace-pre-wrap">{site.bio}</div>
            </section>
          )}
          {(designations.length > 0 || facts.length > 0) && (
            <section data-testid="agent-site-credentials">
              {designations.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {designations.map((d, i) => (
                    <span key={i} className="text-sm font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: '#070B5F', color: '#070B5F' }}>{d}</span>
                  ))}
                </div>
              )}
              {facts.length > 0 && <div className="mt-3 text-sm text-slate-600">{facts.join(' · ')}</div>}
            </section>
          )}
          {site.featured_areas?.length > 0 && (
            <section>
              <h2 className="font-serif text-xl font-bold mb-3" style={{ color: '#070B5F' }}>Areas Served</h2>
              <div className="flex flex-wrap gap-2">
                {site.featured_areas.map((a, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full" style={{ background: '#F3FAE8', color: '#4c7f21' }}><MapPin size={12} /> {a}</span>
                ))}
              </div>
            </section>
          )}
          {site.testimonials?.length > 0 && (
            <section>
              <h2 className="font-serif text-2xl font-bold mb-4" style={{ color: '#070B5F' }}>Client Reviews</h2>
              <div className="space-y-3">
                {site.testimonials.map((t, i) => (
                  <blockquote key={i} className="bg-white rounded-2xl p-5 border-l-4 shadow-sm" style={{ borderColor: '#6BA82E' }}>
                    <p className="text-slate-700 italic">"{t.quote}"</p>
                    <div className="text-xs mt-2 font-semibold" style={{ color: '#070B5F' }}>— {t.name}{t.city && `, ${t.city}`}</div>
                  </blockquote>
                ))}
              </div>
            </section>
          )}
        </div>

        <div>
          {site.lead_capture_enabled && (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm sticky top-6">
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: '#6BA82E' }} />
                  <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Thank you!</div>
                  <div className="text-sm text-slate-500 mt-2">{site.display_name} will reach out shortly.</div>
                </div>
              ) : (
                <>
                  <div className="font-serif text-xl font-bold mb-1" style={{ color: '#070B5F' }}>Let's Connect</div>
                  <div className="text-xs text-slate-500 mb-4">Send a quick message — no pressure.</div>
                  <form onSubmit={submit} className="space-y-2">
                    <Field id="agent-first-name" label="First name"><input id="agent-first-name" required autoComplete="given-name" value={form.first_name} onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))} className={INPUT} /></Field>
                    <Field id="agent-last-name" label="Last name"><input id="agent-last-name" autoComplete="family-name" value={form.last_name} onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))} className={INPUT} /></Field>
                    <Field id="agent-email" label="Email"><input id="agent-email" required type="email" autoComplete="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className={INPUT} /></Field>
                    <Field id="agent-phone" label="Phone"><PhoneInput id="agent-phone" value={form.phone} onChange={(d) => setForm(f => ({ ...f, phone: d }))} className={INPUT} placeholder="Phone" /></Field>
                    <Field id="agent-message" label="How can I help?"><textarea id="agent-message" value={form.message} onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} rows={3} className={INPUT} /></Field>
                    <button type="submit" className="w-full py-2.5 rounded-lg font-semibold text-white" style={{ background: '#6BA82E' }} data-testid="agent-site-submit">Send Message</button>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* PUBLIC landing page at /lp/:slug */
export function LandingPagePublic() {
  const { slug } = useParams();
  const [lp, setLp] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({});
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/re/lp/${slug}`).then(r => setLp(r.data)).catch((e) => setErr(loadError(e, 'page')));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try { const r = await axios.post(`${BACKEND}/api/re/lp/${slug}/submit`, form); setSubmitted(r.data.thank_you_message); }
    catch (e) { toast({ title: 'Submission failed', description: isLinkPaused(e) ? PAGE_PAUSED_COPY : 'Please try again in a moment.' }); }
  };

  if (err) return <Notice>{err}</Notice>;
  if (!lp) return <div className="min-h-screen flex items-center justify-center" style={{ background: PAGE_BG }}><Loader2 className="animate-spin" /></div>;

  // A page saved with no fields still gets a usable form.
  const fields = lp.form_fields?.length ? lp.form_fields : LP_DEFAULT_FIELDS;
  const heroImage = safeHref(lp.hero_image_url);

  return (
    <div className="min-h-screen" style={{ background: '#faf7f2' }}>
      <div className="max-w-4xl mx-auto px-6 py-16">
        {heroImage && <img src={heroImage} alt="" className="w-full max-h-72 object-cover rounded-2xl mb-10" data-testid="lp-hero-image" />}
        <div className="text-center">
          <h1 className="font-serif text-4xl md:text-5xl font-bold" style={{ color: '#070B5F' }}>{lp.headline}</h1>
          {lp.subheadline && <p className="text-lg text-slate-600 mt-3 max-w-2xl mx-auto">{lp.subheadline}</p>}
        </div>

        {lp.features?.length > 0 && (
          <div className="grid md:grid-cols-3 gap-4 mt-10">
            {lp.features.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                <div className="font-serif font-bold" style={{ color: '#070B5F' }}>{f.title}</div>
                <div className="text-sm text-slate-600 mt-1">{f.description}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 max-w-md mx-auto bg-white rounded-2xl p-6 border border-slate-100 shadow">
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle2 size={48} className="mx-auto mb-3" style={{ color: '#6BA82E' }} />
              <div className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>{submitted}</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              {fields.map(f => {
                const label = f === 'phone' ? 'Phone' : f.charAt(0).toUpperCase() + f.slice(1).replace(/_/g, ' ');
                const id = `lp-${f}`;
                return (
                  <Field key={f} id={id} label={label}>
                    {f === 'phone' ? (
                      <PhoneInput id={id} value={form.phone || ''} onChange={(d) => setForm(x => ({ ...x, phone: d }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm h-auto" data-testid="lp-field-phone" />
                    ) : (
                      <input id={id} required={f === 'email'} type={f === 'email' ? 'email' : 'text'}
                        autoComplete={f === 'email' ? 'email' : f === 'name' ? 'name' : undefined}
                        value={form[f] || ''} onChange={(e) => setForm(x => ({ ...x, [f]: e.target.value }))}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid={`lp-field-${f}`} />
                    )}
                  </Field>
                );
              })}
              <button type="submit" className="w-full py-3 rounded-lg font-semibold text-white text-sm" style={{ background: '#6BA82E' }} data-testid="lp-submit">{lp.cta_label}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
